/**
 * Editing the stored payload of a DATABASE template (a ``planosfera.database.v1`` snapshot in
 * ``payload.snapshot``). Pure functions over plain JSON. Columns, options and views refer to each
 * other by key, so every operation that removes or retypes something also repairs what points at
 * it - sample values and shared views - instead of leaving a snapshot the importer would refuse.
 */
export type Json = Record<string, unknown>;

export const OPTION_TYPES: readonly string[] = ['single_select', 'multi_select', 'status', 'tags'];
const MULTI_TYPES: readonly string[] = ['multi_select', 'tags'];

const list = (value: unknown): Json[] => (Array.isArray(value) ? (value as Json[]) : []);
const str = (value: unknown): string => (typeof value === 'string' ? value : '');

export function snapshotOf(payload: Json): Json {
  return (payload['snapshot'] ?? {}) as Json;
}

export const tablesOf = (payload: Json): Json[] => list(snapshotOf(payload)['tables']);
export const columnsOf = (table: Json): Json[] => list(table['columns']);
export const recordsOf = (table: Json): Json[] => list(table['records']);
export const viewsOf = (table: Json): Json[] => list(table['views']);

/** The same payload with table `index` replaced. */
function withTable(payload: Json, index: number, change: (table: Json) => Json): Json {
  const snapshot = snapshotOf(payload);
  return {
    ...payload,
    snapshot: {
      ...snapshot,
      tables: tablesOf(payload).map((t, i) => (i === index ? change(t) : t)),
    },
  };
}

export function setDatabaseMeta(payload: Json, meta: { name: string; description: string }): Json {
  const snapshot = snapshotOf(payload);
  const description = meta.description.trim();
  return {
    ...payload,
    snapshot: {
      ...snapshot,
      database: {
        ...((snapshot['database'] ?? {}) as Json),
        name: meta.name.trim(),
        description: description === '' ? null : description,
      },
    },
  };
}

// --- tables ------------------------------------------------------------------------------------

export function renameTable(payload: Json, index: number, name: string): Json {
  return withTable(payload, index, (t) => ({ ...t, name: name.trim() }));
}

export function moveTable(payload: Json, index: number, by: -1 | 1): Json {
  const tables = [...tablesOf(payload)];
  const [item] = tables.splice(index, 1);
  if (!item) return payload;
  tables.splice(Math.min(Math.max(index + by, 0), tables.length), 0, item);
  return { ...payload, snapshot: { ...snapshotOf(payload), tables } };
}

/** A database keeps at least one table. */
export function removeTable(payload: Json, index: number): Json {
  const tables = tablesOf(payload);
  if (tables.length <= 1) return payload;
  return {
    ...payload,
    snapshot: { ...snapshotOf(payload), tables: tables.filter((_, i) => i !== index) },
  };
}

export function addTable(payload: Json, name: string): Json {
  const tables = tablesOf(payload);
  const key = freshKey(
    't',
    tables.map((t) => str(t['key'])),
  );
  const table = {
    key,
    name: name.trim(),
    columns: [{ key: 'name', name: 'Name', type: 'text' }],
    views: [],
  };
  return { ...payload, snapshot: { ...snapshotOf(payload), tables: [...tables, table] } };
}

// --- columns -----------------------------------------------------------------------------------

export function freshKey(prefix: string, used: Iterable<string>): string {
  const taken = new Set(used);
  for (let n = 1; ; n += 1) {
    const key = `${prefix}${String(n)}`;
    if (!taken.has(key)) return key;
  }
}

export function renameColumn(payload: Json, table: number, key: string, name: string): Json {
  return withTable(payload, table, (t) => ({
    ...t,
    columns: columnsOf(t).map((c) => (c['key'] === key ? { ...c, name: name.trim() } : c)),
  }));
}

export function moveColumn(payload: Json, table: number, index: number, by: -1 | 1): Json {
  return withTable(payload, table, (t) => {
    const columns = [...columnsOf(t)];
    const [item] = columns.splice(index, 1);
    if (!item) return t;
    columns.splice(Math.min(Math.max(index + by, 0), columns.length), 0, item);
    return { ...t, columns };
  });
}

export function addColumn(payload: Json, table: number, name: string, type = 'text'): Json {
  return withTable(payload, table, (t) => ({
    ...t,
    columns: [
      ...columnsOf(t),
      {
        key: freshKey(
          'col',
          columnsOf(t).map((c) => str(c['key'])),
        ),
        name: name.trim(),
        type,
      },
    ],
  }));
}

/** A view without anything that names `column` (filters, sorts, hidden/ordered/card fields). */
function viewWithout(view: Json, column: string, filtersOnly: boolean): Json {
  const config = { ...((view['config'] ?? {}) as Json) };
  config['filters'] = list(config['filters']).filter((f) => f['column'] !== column);
  if (filtersOnly) {
    if (config['group'] === column) config['group'] = null;
    return { ...view, config };
  }
  config['sort'] = list(config['sort']).filter((s) => s['column'] !== column);
  for (const field of ['hidden', 'order', 'card_fields']) {
    if (Array.isArray(config[field])) {
      config[field] = (config[field] as string[]).filter((k) => k !== column);
    }
  }
  for (const field of ['group', 'title']) if (config[field] === column) config[field] = null;
  return { ...view, config };
}

function withoutCells(records: Json[], key: string): Json[] {
  return records.map((r) => {
    const cells = (r['cells'] ?? {}) as Json;
    if (!(key in cells)) return r;
    return { ...r, cells: Object.fromEntries(Object.entries(cells).filter(([k]) => k !== key)) };
  });
}

/** How many sample cells hold a value in this column (what removing or retyping would clear). */
export function filledCells(table: Json, key: string): number {
  return recordsOf(table).filter((r) => {
    const value = ((r['cells'] ?? {}) as Json)[key];
    return value != null && value !== '' && !(Array.isArray(value) && value.length === 0);
  }).length;
}

/** A table keeps at least one column. Its sample values and everything in a view that names it go. */
export function removeColumn(payload: Json, table: number, key: string): Json {
  return withTable(payload, table, (t) => {
    if (columnsOf(t).length <= 1) return t;
    return {
      ...t,
      columns: columnsOf(t).filter((c) => c['key'] !== key),
      records: withoutCells(recordsOf(t), key),
      views: viewsOf(t).map((v) => viewWithout(v, key, false)),
    };
  });
}

/**
 * A new type starts from that type's empty configuration; the column's sample values are cleared
 * (they were written for the old type) and view filters on it dropped (their operators and values
 * belonged to the old type). Sorts, order and visibility stay.
 */
export function changeColumnType(payload: Json, table: number, key: string, type: string): Json {
  return withTable(payload, table, (t) => {
    const column = columnsOf(t).find((c) => c['key'] === key);
    if (!column || column['type'] === type) return t;
    return {
      ...t,
      columns: columnsOf(t).map((c) => {
        if (c['key'] !== key) return c;
        return { ...c, type, config: OPTION_TYPES.includes(type) ? { options: [] } : {} };
      }),
      records: withoutCells(recordsOf(t), key),
      views: viewsOf(t).map((v) => viewWithout(v, key, true)),
    };
  });
}

// --- select options ----------------------------------------------------------------------------

export const optionsOf = (column: Json): Json[] =>
  list(((column['config'] ?? {}) as Json)['options']);

function withOptions(
  payload: Json,
  table: number,
  key: string,
  change: (options: Json[]) => Json[],
): Json {
  return withTable(payload, table, (t) => ({
    ...t,
    columns: columnsOf(t).map((c) =>
      c['key'] === key
        ? { ...c, config: { ...((c['config'] ?? {}) as Json), options: change(optionsOf(c)) } }
        : c,
    ),
  }));
}

export function addOption(payload: Json, table: number, key: string, label: string): Json {
  return withOptions(payload, table, key, (options) => [
    ...options,
    {
      key: freshKey(
        'opt',
        options.map((o) => str(o['key'])),
      ),
      label: label.trim(),
    },
  ]);
}

export function renameOption(
  payload: Json,
  table: number,
  key: string,
  optionKey: string,
  label: string,
): Json {
  return withOptions(payload, table, key, (options) =>
    options.map((o) => (o['key'] === optionKey ? { ...o, label: label.trim() } : o)),
  );
}

/** Removing an option clears it from sample cells and drops the view filters on the column. */
export function removeOption(payload: Json, table: number, key: string, optionKey: string): Json {
  const without = withOptions(payload, table, key, (options) =>
    options.filter((o) => o['key'] !== optionKey),
  );
  return withTable(without, table, (t) => {
    const column = columnsOf(t).find((c) => c['key'] === key);
    const multi = MULTI_TYPES.includes(str(column?.['type']));
    return {
      ...t,
      records: recordsOf(t).map((r) => {
        const cells = (r['cells'] ?? {}) as Json;
        const value = cells[key];
        if (multi && Array.isArray(value)) {
          return {
            ...r,
            cells: { ...cells, [key]: (value as string[]).filter((v) => v !== optionKey) },
          };
        }
        if (value === optionKey) {
          return {
            ...r,
            cells: Object.fromEntries(Object.entries(cells).filter(([k]) => k !== key)),
          };
        }
        return r;
      }),
      views: viewsOf(t).map((v) => viewWithout(v, key, true)),
    };
  });
}

// --- views and sample records ------------------------------------------------------------------

export function renameView(payload: Json, table: number, index: number, name: string): Json {
  return withTable(payload, table, (t) => ({
    ...t,
    views: viewsOf(t).map((v, i) => (i === index ? { ...v, name: name.trim() } : v)),
  }));
}

export function removeView(payload: Json, table: number, index: number): Json {
  return withTable(payload, table, (t) => ({
    ...t,
    views: viewsOf(t).filter((_, i) => i !== index),
  }));
}

export function removeRecord(payload: Json, table: number, index: number): Json {
  return withTable(payload, table, (t) => ({
    ...t,
    records: recordsOf(t).filter((_, i) => i !== index),
  }));
}

export function clearRecords(payload: Json, table: number): Json {
  return withTable(payload, table, (t) => ({ ...t, records: [] }));
}

/** Problems the form shows before saving (names the importer would refuse). */
export function databaseTemplateProblems(payload: Json): string[] {
  const problems: string[] = [];
  const snapshot = snapshotOf(payload);
  if (str(((snapshot['database'] ?? {}) as Json)['name']).trim() === '') problems.push('name');
  const tables = tablesOf(payload);
  const tableNames = tables.map((t) => str(t['name']).trim().toLowerCase());
  if (tableNames.some((n) => n === '')) problems.push('tableName');
  if (new Set(tableNames).size !== tableNames.length) problems.push('tableDuplicate');
  for (const table of tables) {
    const names = columnsOf(table).map((c) => str(c['name']).trim().toLowerCase());
    if (names.some((n) => n === '')) problems.push('columnName');
    if (new Set(names).size !== names.length) problems.push('columnDuplicate');
    for (const column of columnsOf(table)) {
      const labels = optionsOf(column).map((o) => str(o['label']).trim().toLowerCase());
      if (labels.some((n) => n === '')) problems.push('optionLabel');
      if (new Set(labels).size !== labels.length) problems.push('optionDuplicate');
    }
    const views = viewsOf(table).map((v) => str(v['name']).trim().toLowerCase());
    if (views.some((n) => n === '')) problems.push('viewName');
    if (new Set(views).size !== views.length) problems.push('viewDuplicate');
  }
  return [...new Set(problems)];
}
